import { unwrapResult } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { PostParams, requestOK } from "redux-thunk-kit";
import { AuthApiItem, AuthEntity, NormalizedAuth } from "~/types/models/auth";
import { ENDPOINTS, ERROR_STATUS_CODE, HeadshotStyleProps } from "~/constants";
import { removeTokenRequestHeader, setTokenRequestHeader } from "~/utils/request";
import * as storage from "~/utils/storage";
import { AppThunk } from "~/redux/root-store";
import { setCurrentUser } from "~/redux/auth/slice";
import { authSchema } from "~/redux/auth/entity-config";
import { saveUserCredential } from "~/services/auth";
import { thunk } from "~/utils/thunk";
import { fetchCurrentUserInfo } from "~/redux/users/thunk";
import { AppError } from "ry-error-wrapper";
import { DASHBOARD_STORAGE_KEYS } from "~/constants/dashboard";
import { fetchMyListJob, fetchOutfits, fetchPendingJob } from "~/redux/dashboards/thunk";
import {
  ConfirmKeyFeature,
  setConfirmKeyFeature,
  setConfirmKeyFeature2,
  setConfirmSurvey,
  setStyleData,
} from "~/redux/dashboards/slice";
import { removeLocalCredential, removeUserDashboardStorage } from "~/utils/helpers";

export interface LoginByEmailParams extends PostParams {
  data: {
    email: string;
    password: string;
    rememberMe: boolean;
  };
}

export interface RegisterParams extends PostParams {
  data: {
    email: string;
    name?: string;
    password: string;
    redirect?: string;
    recaptchaToken: string | null;
  };
}

export interface SendMailVerification extends PostParams {
  data: {
    email: string;
  };
}

export interface VerifyEmailCode extends PostParams {
  data: {
    code: string;
    uid: string;
  };
}

export interface SendMailResetPwd extends PostParams {
  data: {
    email: string;
  };
}

export interface VerifyPasscodeResetPwd extends PostParams {
  data: {
    code: string;
    uid: string;
  };
}

export interface ResetPwd extends PostParams {
  data: {
    password: string;
    code: string;
    uid: string;
  };
}

export interface SocialLoginParams extends PostParams {
  data: {
    provider: string;
    token: string;
    flow: "auth-code" | "implicit" | "default-button";
    clientId: string;
    name?: string;
    preferredName?: string;
    profilePicture?: string;
  };
}

const query = thunk.query<AuthEntity, NormalizedAuth>("auth", authSchema);

const loadDataFromStorage = async (dispatch: any) => {
  // const storedDashboardProgress = await storage.load(DASHBOARD_STORAGE_KEYS.DASHBOARD_PROGRESS);
  // const currentJobId: string = await storage.load(DASHBOARD_STORAGE_KEYS.JOB_ID);
  const currentKeyFeature: ConfirmKeyFeature = await storage.load(DASHBOARD_STORAGE_KEYS.KEY_FEATURE);
  const currentKeyFeature2: ConfirmKeyFeature = await storage.load(DASHBOARD_STORAGE_KEYS.KEY_FEATURE_2);
  const currentStyle: HeadshotStyleProps[] = await storage.load(DASHBOARD_STORAGE_KEYS.STYLE_SELECTION);

  // const currentVerifyResult = await storage.load(DASHBOARD_STORAGE_KEYS.VERIFY_RESULT);
  const currentSurveyIds = await storage.load(DASHBOARD_STORAGE_KEYS.SURVEY_IDS);

  if (currentKeyFeature) {
    dispatch(setConfirmKeyFeature(currentKeyFeature));
  }
  if (currentKeyFeature2) {
    dispatch(setConfirmKeyFeature2(currentKeyFeature2));
  }
  if (currentStyle) {
    dispatch(setStyleData(currentStyle));
  }

  if (currentSurveyIds) {
    dispatch(setConfirmSurvey(currentSurveyIds));
  }
};

export const onLoginSuccessMisc = query.wrapper<AuthEntity>("onLoginSuccess", async ({ token, user }, { dispatch }) => {
  let refreshUser = user;
  setTokenRequestHeader(token.access_token);
  await Promise.all([
    dispatch(fetchCurrentUserInfo({})),
    dispatch(fetchMyListJob({})),
    dispatch(fetchPendingJob({})),
    dispatch(fetchOutfits({})),
  ]).then(async ([fetchUserResponse]) => {
    if (requestOK(fetchUserResponse, fetchCurrentUserInfo)) {
      // @ts-ignore
      const { apiResponseData } = unwrapResult(fetchUserResponse);
      refreshUser = apiResponseData;

      await saveUserCredential({
        user: refreshUser,
        accessToken: token.access_token,
        token,
      });

      // @ts-ignore
      Sentry.setUser(refreshUser);
      Sentry.setTag("email", refreshUser?.email);

      // await dispatch(fetchMyListJob({}));
      // await dispatch(fetchPendingJob({}));
      await loadDataFromStorage(dispatch);

      // We set current user to redux at the end to avoid re-rendering of the Private Routes too early
      await dispatch(setCurrentUser({ user: refreshUser, token }));

      return true;
    }
    return false;
  });
  // const resAction = await dispatch(fetchCurrentUserInfo({}));

  return true;
});

export const loginByEmail = query.post<LoginByEmailParams>(ENDPOINTS.LOGIN, "byEmailReq", {
  includeApiResponseData: true,
});
export const socialLogin = query.post<SocialLoginParams>(ENDPOINTS.SOCIAL_LOGIN, "bySocialReq", {
  includeApiResponseData: true,
});

export const register = query.post<RegisterParams, { id: string }>(ENDPOINTS.REGISTER, "register", {
  includeApiResponseData: true,
});

export const userLogout = query.post(ENDPOINTS.LOGOUT, "userLogout");

export const sendMailVerification = query.post<SendMailVerification>(
  ENDPOINTS.SEND_EMAIL_VERIFICATION,
  "sendMailVerification",
  {
    includeApiResponseData: true,
  }
);

export const verifyEmailCode = query.post<VerifyEmailCode>(ENDPOINTS.VERIFY_EMAIL_CODE, "verifyEmailCode", {
  includeApiResponseData: true,
});

export const sendMailResetPwd = query.post<SendMailResetPwd>(ENDPOINTS.SEND_MAIL_RESET_PWD, "sendMailResetPwd", {
  includeApiResponseData: true,
});

export const verifyPasscodeResetPwd = query.post<VerifyPasscodeResetPwd>(
  ENDPOINTS.VERIFY_PASSCODE_RESET_PWD,
  "verifyPasscodeResetPwd",
  {
    includeApiResponseData: true,
  }
);

export const resetPwd = query.post<ResetPwd>(ENDPOINTS.RESET_PWD, "resetPwd", {
  includeApiResponseData: true,
});

const parseAuthApiData = (resAction: any): AuthApiItem => {
  const { apiResponseData } = unwrapResult(resAction);
  return apiResponseData;
};

export const verifyEmailCodeMisc = query.wrapper<VerifyEmailCode>(
  "verifyEmailCodeMisc",
  async (params, { dispatch, rejectWithValue }) => {
    const resAction = await dispatch(verifyEmailCode(params));
    if (requestOK(resAction, verifyEmailCode)) {
      const authApiItem = parseAuthApiData(resAction);
      await dispatch(onLoginSuccessMisc(authApiItem));
      return resAction;
    }
    if (
      resAction.payload.errStatusCode === 401 &&
      resAction.payload.errCode === ERROR_STATUS_CODE.NOT_VERIFIED_ACCOUNT
    ) {
      return resAction.payload;
    }
    throw new AppError("", "", { shouldReport: resAction.payload.errStatusCode === 500 });
  }
);

export const loginByEmailMisc = query.wrapper<LoginByEmailParams>(
  "loginByEmailMisc",
  async (params, { dispatch, rejectWithValue }) => {
    const resAction = await dispatch(loginByEmail(params));
    if (requestOK(resAction, loginByEmail)) {
      const authApiItem = parseAuthApiData(resAction);
      await dispatch(onLoginSuccessMisc(authApiItem));
      return resAction;
    }
    if (
      resAction.payload.errStatusCode === 401 &&
      resAction.payload.errCode === ERROR_STATUS_CODE.NOT_VERIFIED_ACCOUNT
    ) {
      return resAction.payload;
    }
    throw new AppError("", "", { shouldReport: resAction.payload.errStatusCode === 500 });
  }
);

export const socialLoginMisc = query.wrapper<SocialLoginParams>("loginBySocial", async (params, { dispatch }) => {
  const resAction = await dispatch(socialLogin(params));
  if (requestOK(resAction, socialLogin)) {
    const authApiItem = parseAuthApiData(resAction);
    await dispatch(onLoginSuccessMisc(authApiItem));
  }
});

export const logout = (): AppThunk => async dispatch => {
  const resAction = await dispatch(userLogout({ data: {} }));
  // Remove all storage values despite the result of Log out API. Because sometimes, this API is call twice.
  // if (requestOK(resAction, userLogout)) {
  removeLocalCredential();
  removeUserDashboardStorage();
  removeTokenRequestHeader();
  dispatch({ type: "USER_LOGOUT" });
  // }
};
